import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, distinctUntilChanged, map, noop } from 'rxjs';

import { EvaluationContent } from '../models/evaluation.model';
import { FileQueryParams, Result, ResultQueryParams, ResultWithLatest, Results } from '../models/result.model';
import { DistinctSubject, recursiveQuery } from '../models/utility.model';
import { AuthUsecase } from '../usecases/auth.usecase';
import { ResultGateway } from '../usecases/result.gateway';
import { ResultUsecase } from '../usecases/result.usecase';

@Injectable()
export class ResultInteractor extends ResultUsecase {
  get resultWithLatests$(): Observable<ResultWithLatest[]> {
    return this._results.pipe(map(results => this.addLatest(results.values())));
  }

  private readonly _results = new DistinctSubject<Results>(new Results());

  constructor(
    private _authUsecase: AuthUsecase,
    private _resultGateway: ResultGateway,
  ) {
    super();

    this._authUsecase.authState$
      .pipe(
        map(({ status }) => status === 'signedIn'),
        distinctUntilChanged(),
      )
      .subscribe(signedIn => (signedIn ? noop : this.onSignOut()));
  }

  listResults(params: ResultQueryParams): Observable<never> {
    const result = new AsyncSubject<never>();
    recursiveQuery(queryParams => this._resultGateway.listResults(queryParams), params).subscribe({
      next: results => this._results.next(new Results(results)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  getEvaluation(projectId: string, unitId: string, resultId: string): Observable<EvaluationContent[]> {
    return this._resultGateway.getEvaluation(projectId, unitId, resultId);
  }

  getFile(params: FileQueryParams): Observable<string> {
    const result = new AsyncSubject<string>();
    this._resultGateway.getFile(params).subscribe({
      next: ({ url }) => result.next(url),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  clearResults(): void {
    this._results.next(new Results());
  }

  private onSignOut(): void {
    this._results.next(new Results());
  }

  private addLatest(results: Result[]): ResultWithLatest[] {
    if (!results.length) {
      return [];
    }
    const sorted = [...results].sort((a, b) => b.resultId.localeCompare(a.resultId));
    let latestResult = sorted[0];
    let isBacked = false;
    const latestIds = new Set<string>();

    while (latestResult.prev !== 'none') {
      if (latestResult.redo === 'manual') {
        isBacked = true;
      } else {
        if (!isBacked) {
          latestIds.add(latestResult.resultId);
        }
        isBacked = false;
      }
      const prevResult = results.find(result => result.resultId === latestResult.prev);
      if (!prevResult) {
        break;
      }
      latestResult = prevResult;
    }
    if (!isBacked) {
      latestIds.add(latestResult.resultId);
    }
    const filterStatus: ResultWithLatest[] = results.map(result => ({
      ...result,
      isLatest: latestIds.has(result.resultId),
    }));
    return filterStatus;
  }
}
